import { useEffect, useState, useRef } from "react";
import { useDebounce } from "use-debounce";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import {
  EntityType,
  ServiceRequestService,
  ServiceRequestEntityTypesEnum,
} from "@propertelligent/client-api";
import ServiceRequestTopHeader from "../../newComponents/listings/top-listing-headers/service-requests-listing-top-header";
import ServiceRequestCard from "../../newComponents/listings/listing-cards/service-requests";
import AddServiceRequestForm from "../../newComponents/serviceRequest/AddServiceRequestForm";
import { Route, Routes, useLocation } from "react-router-dom";
import ServiceRequestDetails from "./ServiceRequestDetails";
import { UserLoginData } from "../../components/common/charts/Enums";
import apiHelper from "../../utils/apiHelper";
import { usePermissions } from "../../hooks/usePermissions";
import NoAccess from "../NoAccess";
import { PermissionGuard } from "../../newComponents/PermissionGuard";
import { AccessLevelEnum } from "@propertelligent/client-api";

interface ServiceRequestProps {
  isEntity?: boolean;
  entityTypeId?: EntityType | any;
  entityId?: number;
  className?: string;
  parentClassName?: string;
  onServiceRequestAdded?: () => void; // Add the callback prop
  userRole?: "Landlord" | "Tenant";
  tenantUnitId?: number | null;
}

const ServiceRequest: React.FC<ServiceRequestProps> = ({
  isEntity = false,
  entityTypeId,
  entityId,
  className = "",
  onServiceRequestAdded,
  userRole = "Landlord",
  parentClassName,
  tenantUnitId,
}) => {
  const { hasAccess, canWrite } = usePermissions();
  const { search, pathname } = useLocation();
  const queryParams = new URLSearchParams(search);
  const initialSelectedOption = queryParams.get("selectedOption") || "New";

  const getFilterString = (filter: string, search: string) => {
    let baseFilter = "";
    switch (filter) {
      case "New":
        baseFilter = "ServiceRequestStatus == 1 || ServiceRequestStatus == 2";
        break;
      case "In Progress":
        baseFilter = "ServiceRequestStatus == 3";
        break;
      case "Fixed":
        baseFilter = "ServiceRequestStatus == 4 || ServiceRequestStatus == 5";
        break;
      case "Completed":
        baseFilter = "ServiceRequestStatus == 6";
        break;
      default:
        baseFilter = "";
        break;
    }
    const searchFilter = search
      ? `Description.ToLower().Contains("${search.toLowerCase()}")`
      : "";
    return [baseFilter ? `(${baseFilter})` : "", searchFilter].filter(Boolean).join(" && ");
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState<string | null>(getFilterString(initialSelectedOption, ""));
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [activeFilter, setActiveFilter] = useState(initialSelectedOption);
  const [isModalOpen, setModalOpen] = useState(false);
  const [hasAnyRequests, setHasAnyRequests] = useState(false);
  const [searchOnlyFilter, setSearchOnlyFilter] = useState<string | null>(null);

  const isFirstRender = useRef(true);


  useEffect(() => {
    const params = new URLSearchParams(search);
    const option = params.get("selectedOption") || "New";
    setActiveFilter(option);
  }, [search]);
  const handleSort = (newDirection: "asc" | "desc") => {
    setSortDirection(newDirection);
  };

  useEffect(() => {
  if (isFirstRender.current) {
    isFirstRender.current = false;
    return;
  }

  if (!pathname.includes("servicerequestdetails")) {
    fetchData(1); 
    fetchGlobalAggregates(); 
  }
}, [pathname]); 



  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter); // Set active filter for styling/logic
    updateFullyQualifiedFilter(filter, searchTerm);
  };

  const updateFullyQualifiedFilter = (filter: string, search: string) => {
    const searchFilter = search
      ? `Description.ToLower().Contains("${search.toLowerCase()}")`
      : "";

    setSearchOnlyFilter(searchFilter || null); // Update search-only filter for aggregates
    setFullyQualifiedFilter(getFilterString(filter, search) || null);
  };

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  const getServiceRequestListFn = () => {

    if (isEntity && entityTypeId !== undefined && entityId !== undefined) {
      if (entityTypeId === EntityType.Property) {
        // Assuming 1 = Property
        return (params) =>
          ServiceRequestService.getApiServiceRequestPropertylist(
            entityId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            fullyQualifiedFilter,
            `RequestDate ${sortDirection}`
          );
      } else if (entityTypeId === EntityType.Unit) {
        // Assuming 1 = Property
        return (params) =>
          ServiceRequestService.getApiServiceRequestUnitlist(
            entityId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            fullyQualifiedFilter,
            `RequestDate ${sortDirection}`
          );
      } else if (entityTypeId == EntityType.Tenant) {
        // Assuming 1 = Property
        return (params) =>
          ServiceRequestService.getApiServiceRequestTenantlist(
            entityId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            fullyQualifiedFilter,
            `RequestDate ${sortDirection}`
          );
      } else if (entityTypeId == EntityType.Contractor) {
        // Assuming 1 = Property
        return (params) =>
          ServiceRequestService.getApiServiceRequestContractorlist(
            entityId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            fullyQualifiedFilter,
            `RequestDate ${sortDirection}`
          );
      }
      // Add more entity type cases here as needed
    }

    return ServiceRequestService.getApiServiceRequestOrganizationlist;
  };

  const {
    rows,
    loading,
    hasMore,
    rowCount,
    fetchData,
    currentPage,
  } = infiniteScrollHook({
    httpFetchDataFn: getServiceRequestListFn(),
    FullyQualifiedFilters: fullyQualifiedFilter,
    pageSize: 10,
    FullyQualifiedSorts: `RequestDate ${sortDirection}`,
  });

  // Direct API call for global aggregate counts (search only, no status filter)
  const [globalAggregateValues, setGlobalAggregateValues] = useState<Record<string, number> | undefined>();

  const fetchGlobalAggregates = async () => {
    const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || "0");
    const tenantId = parseInt(sessionStorage.getItem(UserLoginData.tenant_id) || "0");
    const reqID = orgId || tenantId;

    if (reqID) {
      try {
        let apiCall = ServiceRequestService.getApiServiceRequestOrganizationlist;
        let callEntityId = reqID;

        if (isEntity && entityTypeId !== undefined && entityId !== undefined) {
          callEntityId = entityId;
             if (entityTypeId === EntityType.Property) apiCall = ServiceRequestService.getApiServiceRequestPropertylist;
             else if (entityTypeId === EntityType.Unit) apiCall = ServiceRequestService.getApiServiceRequestUnitlist;
             else if (entityTypeId === EntityType.Tenant) apiCall = ServiceRequestService.getApiServiceRequestTenantlist;
             else if (entityTypeId === EntityType.Contractor) apiCall = ServiceRequestService.getApiServiceRequestContractorlist;
        }

        const { data } = await apiHelper(
          apiCall,
          { showNotification: false },
          callEntityId,
          1, // pageSize 1
          1, // currentPage 1
          undefined,
          undefined,
          undefined,
          searchOnlyFilter, // Search only!
          `RequestDate ${sortDirection}`
        );
        setGlobalAggregateValues(data?.aggregateValues);
      } catch (e) {
        console.error("Error fetching global aggregates:", e);
      }
    }
  };

  useEffect(() => {
    // Only debounce if there is a search filter, otherwise fetch immediately for initial load
    if (searchOnlyFilter) {
      const timer = setTimeout(() => {
        fetchGlobalAggregates();
      }, 500);
      return () => clearTimeout(timer);
    } else {
      fetchGlobalAggregates();
    }
  }, [searchOnlyFilter]);

  const totalGlobalCount = globalAggregateValues 
    ? Object.values(globalAggregateValues).reduce((acc: number, curr: any) => acc + Number(curr), 0)
    : 0;

  // Effect to capture the initial aggregate counts before any filters are applied
  // useEffect(() => {
  //   if (aggregateValues && !aggregateData && !fullyQualifiedFilter) {
  //     setAggregateData(aggregateValues);
  //   }
  // }, [aggregateValues, aggregateData, fullyQualifiedFilter]);

  useEffect(() => {
    updateFullyQualifiedFilter(activeFilter, debouncedSearchTerm);
  }, [debouncedSearchTerm, activeFilter]);

  // Modified to call onServiceRequestAdded after fetching data

  useEffect(() => {
    updateFullyQualifiedFilter(activeFilter, debouncedSearchTerm);
  }, [debouncedSearchTerm, activeFilter]);

  const handleServiceRequestAdded = () => {
    fetchData(1); // Refresh service request list
    fetchGlobalAggregates(); // Refresh global counts
    if (onServiceRequestAdded) {
      onServiceRequestAdded(); // Trigger parent refresh
    }
  };

  useEffect(() => {
    if (globalAggregateValues && Object.values(globalAggregateValues).some((val: any) => Number(val) > 0)) {
      setHasAnyRequests(true);
    }
  }, [globalAggregateValues]);


  if (userRole !== "Tenant" && !hasAccess("ServiceRequest", 1)) {
    return <NoAccess />;
  }

  const renderContent = (content: React.ReactNode) => {
    if (userRole === "Tenant") {
      return <>{content}</>;
    }
    return (
      <PermissionGuard
        permission="ServiceRequest"
        level={AccessLevelEnum.ReadOnly}
        fallback={<NoAccess />}
      >
        {content}
      </PermissionGuard>
    );
  };

  return (
    <Routes>
      <Route
        index
        element={renderContent(
            <div className={`content-wrapper container-fluid ${parentClassName}`}>
              {hasAnyRequests && (
                <ServiceRequestTopHeader
                  PageHeading="Service Requests"
                  className={className}
                  Count={totalGlobalCount}
                  ButtonName="Add Service Request"
                  MobileButtonName="Add"
                  showAddButton={
                    entityTypeId === EntityType.Contractor || tenantUnitId <= 0
                      ? false
                      : userRole === "Tenant" ? true : canWrite("ServiceRequest")
                  }
                  setSearchTerm={setSearchTerm}
                  searchTerm={searchTerm}
                  handleSort={handleSort}
                  currentSortDirection={sortDirection}
                  handleFilterChange={handleFilterChange}
                  aggregateValuesFilters={globalAggregateValues}
                  onClick={() => setModalOpen(true)}
                  activeFilter={activeFilter}
                  setActiveFilter={setActiveFilter}
                  isEntityTypeTenant={entityTypeId === EntityType.Tenant}
                />
              )}

              <ServiceRequestCard
                Id="ServiceRequest"
                IsFooter={true}
                Searchlength={searchTerm.length}
                dataHookState={{
                  rows,
                  loading,
                  hasMore,
                  fetchData,
                  currentPage,
                }}
                showEmptyBox={
                  entityTypeId === EntityType.Contractor ? false : true
                }
                userRole={userRole}
                hasAnyRequests={hasAnyRequests}
                onAddClick={() => setModalOpen(true)}
                entityTypeId={entityTypeId}
                onServiceRequestDeleted={fetchGlobalAggregates}
              />

              {isModalOpen && (
                <AddServiceRequestForm
                  isModalOpen={isModalOpen}
                  setModalOpen={setModalOpen}
                  fetchData={handleServiceRequestAdded}
                  isEntity={isEntity}
                  entityTypeId={
                    entityTypeId === EntityType.Property
                      ? ServiceRequestEntityTypesEnum.Property
                      : entityTypeId === EntityType.Unit
                      ? ServiceRequestEntityTypesEnum.Unit
                      : entityTypeId === EntityType.Tenant
                      ? ServiceRequestEntityTypesEnum.Unit
                      : Number(entityTypeId)
                  }
                  entityId={
                    entityTypeId === EntityType.Tenant ? tenantUnitId : entityId
                  }
                />
              )}
            </div>
        )}
      />

      <Route
        path="/servicerequestdetails/:id"
        element={renderContent(<ServiceRequestDetails userRole={userRole} />)}
      />
    </Routes>
  );
};

export default ServiceRequest;